import { Mail, Plus, MessageSquarePlus } from "lucide-react";
import { MadeByRemoteWorkers } from "@/components/MadeByRemoteWorkers";

import { useState } from "react";
import { Link } from "react-router-dom";
import { Mascot } from "@/components/Mascot";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { SubmitCafeDialog } from "@/components/SubmitCafeDialog";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { supabase } from "@/integrations/supabase/client";
import { CITY_PAGE_LINKS } from "@/lib/cityPages";


export function Footer() {
  const [email, setEmail] = useState("");
  const [submitCafeOpen, setSubmitCafeOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("newsletter_signups")
        .insert({ email: email.trim().toLowerCase() });
      if (error) {
        if (error.code === "23505") {
          toast.info("You're already on the list!", {
            description: "We'll let you know when the newsletter launches.",
          });
        } else {
          throw error;
        }
      } else {
        // Send confirmation email
        await supabase.functions.invoke("send-newsletter-confirmation", {
          body: { email: email.trim().toLowerCase() },
        });
        toast.success("You're on the list! 🎉", {
          description: "We'll notify you when our newsletter launches.",
        });
      }
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <footer className="border-t border-border bg-card/40 pb-safe">
        <div className="container max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-20">
          {/* Mobile: Compact layout */}
          <div className="md:hidden mb-8">
            {/* Brand */}
            <div className="flex items-center gap-2.5 mb-4">
              <Link to="/" className="flex items-center gap-2.5">
                <Mascot mood="relaxed" size="md" />
                <span className="font-display text-lg font-bold text-logo">grounded</span>
              </Link>
            </div>
            
            {/* Quick Links */}
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/browse" className="text-muted-foreground hover:text-foreground transition-colors">
                Browse Cafes
              </Link>
              <Link to="/browse" className="text-muted-foreground hover:text-foreground transition-colors">
                Near Me
              </Link>
              {CITY_PAGE_LINKS.map((c) => (
                <Link
                  key={c.slug}
                  to={`/work-cafes/${c.slug}`}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Work cafés in {c.name}
                </Link>
              ))}
            </div>

            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Find your perfect cafe to work from.{" "}
              <span className="whitespace-nowrap">Community-powered</span> reviews for remote workers.
            </p>
            <AppStoreBadge className="w-[140px] mt-3" />
            
            {/* Mobile CTA buttons */}
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSubmitCafeOpen(true)}
                className="text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Suggest Cafe
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setFeedbackOpen(true)}
                className="text-xs"
              >
                <MessageSquarePlus className="h-3.5 w-3.5 mr-1" />
                Feedback
              </Button>
            </div>
          </div>

          {/* Desktop: Full 4-column grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="space-y-5">
              <Link to="/" className="flex items-center gap-3">
                <Mascot mood="relaxed" size="md" />
                <span className="font-display text-2xl font-bold text-logo">grounded</span>
              </Link>
              <p className="text-base text-muted-foreground leading-relaxed max-w-xs">
                Find your perfect cafe to work from.{" "}
                <span className="whitespace-nowrap">Community-powered</span> reviews for remote workers.
              </p>
              <AppStoreBadge className="w-[140px]" />
            </div>

            {/* Discover */}
            <div className="space-y-5">
              <h4 className="font-display text-lg font-bold text-foreground">Discover</h4>
              <ul className="space-y-3.5 text-base">
                <li>
                  <Link to="/browse" className="text-muted-foreground hover:text-foreground transition-colors">
                    Browse Cafes
                  </Link>
                </li>
                <li>
                  <Link to="/browse" className="text-muted-foreground hover:text-foreground transition-colors">
                    Nearby
                  </Link>
                </li>
                <li>
                  <Link to="/favorites" className="text-muted-foreground hover:text-foreground transition-colors">
                    Favorites
                  </Link>
                </li>
                <li>
                  <Link to="/profile" className="text-muted-foreground hover:text-foreground transition-colors">
                    Profile
                  </Link>
                </li>
                {CITY_PAGE_LINKS.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/work-cafes/${c.slug}`}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Work cafés in {c.name}
                    </Link>
                  </li>
                ))}
              </ul>

            </div>

            {/* Contribute */}
            <div className="space-y-5">
              <h4 className="font-display text-lg font-bold text-foreground">Contribute</h4>
              <ul className="space-y-3.5 text-base">
                <li>
                  <button 
                    onClick={() => setSubmitCafeOpen(true)}
                    className="text-muted-foreground hover:text-foreground transition-colors text-left"
                  >
                    Suggest a Cafe
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setFeedbackOpen(true)}
                    className="text-muted-foreground hover:text-foreground transition-colors text-left"
                  >
                    Send Feedback
                  </button>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div className="space-y-5">
              <h4 className="font-display text-lg font-bold text-foreground">Stay in the Loop</h4>
              <p className="text-base text-muted-foreground leading-relaxed">
                Stay in the loop — new cafés, tips & more.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-9 text-sm"
                    required
                  />
                </div>
                <Button type="submit" size="sm" className="h-9 px-3" disabled={isSubmitting}>
                  {isSubmitting ? "..." : "Notify Me"}
                </Button>
              </form>
            </div>
          </div>

          {/* Bottom bar - copyright */}
          <div className="mt-8 md:mt-20 pt-6 md:pt-10 border-t border-border flex flex-col-reverse md:flex-row items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              © {new Date().getFullYear()} Grounded
              <span className="text-border">·</span>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <span className="text-border">·</span>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <span className="text-border">·</span>
              <Link to="/support" className="hover:text-foreground transition-colors">Support</Link>
            </p>
            <MadeByRemoteWorkers />

          </div>
        </div>
      </footer>
      
      <SubmitCafeDialog open={submitCafeOpen} onOpenChange={setSubmitCafeOpen} entryPoint="footer" />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}
